/**
 * T3010 — meja oseb v preizkusu (`trial_max_seats`) in ločnica do plačanih sedežev.
 *
 * Čiste funkcije nad vrstico `seat_capacity` (T1420); namenoma zunaj
 * `useBilling`, ki ga veliko testov nadomesti z mockom.
 */
import type { AxiosError } from 'axios';
import type { SeatCapacity } from '@/hooks/useBilling';

/**
 * T3010: ali vrstica pomeni **plačan** sedež, ki ta hip omejuje (naročnina).
 *
 * Matrika sedežev, kartice modulov in števec zunanjih so pisani za plačane
 * sedeže: `enforced` je tam pomenil »naročeno«. Od T3010 je `enforced` lahko
 * `true` tudi v preizkusu (meja oseb), zato ti porabniki berejo to funkcijo.
 */
export function isPaidSeatEnforced(capacity: SeatCapacity | null | undefined): boolean {
  return !!capacity && capacity.enforced && capacity.reason === 'subscribed';
}

/** Zasedenost mest v preizkusu (T3010). */
export interface TrialSeatLimit {
  used: number;
  total: number;
  free: number;
  /** T3096: koliko od `used` so čakajoča povabila (0, kadar jih ni ali jih zaledje ne pošlje). */
  pending: number;
  /** Pregled T3010 (8): preizkus ali promocija brez naročnine — izbira besedila. */
  reason: 'trial' | 'promo';
}

/**
 * T3010: meja oseb v preizkusu ali promociji brez naročnine (`trial_max_seats`).
 *
 * `null`, kadar meje ni (naročnina, preizkus brez meje, ni podatka).
 */
export function trialSeatLimit(capacity: SeatCapacity | null | undefined): TrialSeatLimit | null {
  if (!capacity || !capacity.enforced || capacity.total === null) return null;
  if (capacity.reason !== 'trial' && capacity.reason !== 'promo') return null;
  const total = capacity.total;
  const free = capacity.free ?? 0;
  const used = capacity.used ?? Math.max(0, total - free);
  return { used, total, free, pending: capacity.pending ?? 0, reason: capacity.reason };
}


/** Kupljeni sedeži naročene organizacije s promocijo (T2996 c, pregled 7). */
export function promoSubscribedSeats(
  capacity: SeatCapacity | null | undefined,
): { used: number; total: number; free: number } | null {
  if (!capacity || capacity.reason !== 'promo_subscribed' || capacity.total === null) return null;
  const total = capacity.total;
  const free = capacity.free ?? 0;
  return { used: capacity.used ?? Math.max(0, total - free), total, free };
}

/** Telo 402 `TRIAL_SEAT_LIMIT_REACHED` (T3010). */
export interface TrialSeatLimitError {
  error: string;
  code: 'TRIAL_SEAT_LIMIT_REACHED';
  module: string;
  module_name?: string;
  used: number;
  limit: number;
  /** Pregled T3010 (8): `promo` = promocija brez naročnine. */
  reason?: 'trial' | 'promo';
  /** T3045: sprejem vabila — lastnik in skrbniki so obveščeni. */
  owner_notified?: boolean;
}

/** T3010: 402 zaradi meje oseb v preizkusu. */
export function isTrialSeatLimitError(err: unknown): err is AxiosError<TrialSeatLimitError> {
  const axiosErr = err as AxiosError<Partial<TrialSeatLimitError>>;
  return (
    axiosErr?.response?.status === 402 &&
    axiosErr.response?.data?.code === 'TRIAL_SEAT_LIMIT_REACHED'
  );
}
